// src/scenes/sales/EditSales.jsx
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import { Formik, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// ---------- Helpers ----------
const getItemSalesPrice = (item) =>
  Number(item?.salePrice ?? item?.unitPrice ?? 0);

// ---------- Bank Options ----------
const bankOptions = [
  "Commercial Bank of Ethiopia",
  "telebirr",
  "Cash",
  "Awash International Bank",
  "Dashen Bank",
  "Bank of Abyssinia",
  "Cooperative Bank of Oromia",
  "Enat Bank",
  "Zemen Bank",
  "Bunna International Bank",
  "Wegagen Bank",
  "Berhan International Bank",
  "Lion International Bank",
  "Nib International Bank",
  "Amhara Bank",
  "Global Bank Ethiopia",
  "Oromia International Bank",
  "Hibret Bank",
  "Abay Bank",
  "Ahadu Bank",
  "Gadaa Bank",
  "Tsedey Bank",
  "Hijra Bank",
  "Siinqee Bank",
  "Shabelle Bank",
  "Rammis Bank",
  "Tsehay Bank",
  "Goh Betoch Bank",
  "Sidama Bank",
  "Omo Bank",
  "ZamZam Bank",
].map((b) => ({ label: b, value: b }));

// ---------- Validation Schema ----------
const SCHEMA = yup.object({
  userId: yup.string().required("User is required"),
  customerId: yup.string().required("Customer is required"),
  warehouseId: yup.string().required("Warehouse is required"),
  credit: yup.boolean().required(),
  tinNo: yup.string().required("TIN Number is required"),
  fsNo: yup.string().required("FS Number is required"),
  machineNo: yup.string().required("Machine Number is required"),
  bank: yup.string().required("Bank selection is required"),
  salesDate: yup.date().required("Sales date is required"),
  description: yup.string().nullable(),
  paidAmount: yup
    .number()
    .typeError("Paid amount must be a number")
    .required("Paid amount is required")
    .min(0, "Paid amount must be 0 or more"),
  items: yup
    .array()
    .of(
      yup.object({
        itemId: yup.string().required("Select an item"),
        sp: yup.number().required(),
        quantity: yup
          .number()
          .required("Quantity is required")
          .min(1, "Quantity must be at least 1"),
        bonus: yup.number().nullable(),
        bonusAmount: yup.number().nullable(),
        sponsor: yup.number().nullable(),
        sponsorAmount: yup.number().nullable(),
      })
    )
    .min(1, "Add at least one item"),
  totalPrice: yup.number().required(),
  vat: yup.number().required(),
  totalPriceFinal: yup.number().required(),
  withholdingAmount: yup.number().required(),
});

export default function EditSales() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(null);
  const mounted = useRef(true);

  // ---------- Logged-in user info ----------
  const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
  const isSalesUser = loggedUser.role === "sales";

  // Load data
  useEffect(() => {
    mounted.current = true;
    loadData();
    return () => {
      mounted.current = false;
    };
  }, [id]);

  const loadData = async () => {
    try {
      const [saleRes, uRes, cRes, iRes, wRes] = await Promise.all([
        api.get(`/api/sales/${id}`),
        api.get("/api/user"),
        api.get("/api/customer"),
        api.get("/api/item"),
        api.get("/api/warehouse"),
      ]);

      if (!mounted.current) return;

      const sale = saleRes.data;
      const arr = (x) => (Array.isArray(x) ? x : []);

      // Set dropdown options
      setUsers(
        arr(uRes.data).filter(
          (u) => String(u?.Role?.name || "").toLowerCase() === "sales"
        )
      );
      setCustomers(arr(cRes.data));
      setItems(arr(iRes.data));
      setWarehouses(arr(wRes.data));

      // Transform backend data to match form structure
      const salesItems = sale.SalesItems || [];

      const formValues = {
        id: sale.id,
        userId: String(sale.userId || ""),
        customerId: String(sale.customerId || ""),
        warehouseId: String(sale.warehouseId || ""),
        credit: Boolean(sale.credit),
        tinNo: sale.tinNo || "",
        fsNo: sale.fsNo || "",
        machineNo: sale.machineNo || "",
        bank: sale.bank || "",
        salesDate: sale.salesDate
          ? new Date(sale.salesDate).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),
        description: sale.description || "",
        paidAmount: Number(sale.paidAmount) || "",
        items: salesItems.map((item) => ({
          itemId: String(item.itemId),
          sp: Number(item.unitPrice) || 0,
          quantity: Number(item.quantity) || 1,
          bonus: Number(item.bonus) || 0,
          bonusAmount: Number(item.bonusAmount) || 0,
          sponsor: Number(item.sponsor) || 0,
          sponsorAmount: Number(item.sponsorAmount) || 0,
        })),
        totalPrice: Number(sale.totalPrice) || 0,
        vat: Number(sale.vat) || 0,
        totalPriceFinal: Number(sale.totalTaxedPrice) || 0,
        withholdingAmount: Number(sale.withholdingAmount) || 0,
      };

      setInitialValues(formValues);
      setLoading(false);
    } catch (err) {
      console.error("Failed to load data:", err);
      setToast({ open: true, msg: "Failed to load sale data", sev: "error" });
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const itemsToSend = values.items.map((i) => ({
        itemId: Number(i.itemId),
        quantity: Number(i.quantity),
        unitPrice: Number(i.sp),
        bonus: Number(i.bonus) || 0,
        bonusAmount: Number(i.bonusAmount) || 0,
        sponsor: Number(i.sponsor) || 0,
        sponsorAmount: Number(i.sponsorAmount) || 0,
      }));

      const totalPrice = itemsToSend.reduce(
        (sum, it) => sum + Number(it.unitPrice) * Number(it.quantity),
        0
      );
      const vat = +(totalPrice * 0.15).toFixed(2);
      const totalPriceFinal = +(totalPrice + vat).toFixed(2);
      const withholdingAmount = +(totalPrice * 0.03).toFixed(2);

      const payload = {
        userId: Number(values.userId),
        warehouseId: Number(values.warehouseId),
        customerId: values.customerId ? Number(values.customerId) : null,
        totalPrice: Number(totalPrice.toFixed(2)),
        totalTaxedPrice: totalPriceFinal,
        paidAmount: Number(values.paidAmount),
        bank: values.bank || "",
        salesDate: new Date(values.salesDate).toISOString(),
        tinNo: values.tinNo,
        fsNo: values.fsNo,
        machineNo: values.machineNo,
        description: values.description || "",
        withholdingAmount,
        vat,
        credit: values.credit,
        items: itemsToSend,
      };

      const res = await api.put(`/api/sales/${id}`, payload);
      setSubmitting(false);

      if (!res || res.status >= 400) {
        const errMsg = res?.data?.message || "Update failed";
        setToast({ open: true, msg: errMsg, sev: "error" });
        return;
      }

      setToast({
        open: true,
        msg: "Sale Updated Successfully!",
        sev: "success",
      });
      setTimeout(() => navigate("/sales/view"), 1000);
    } catch (err) {
      setSubmitting(false);
      setToast({
        open: true,
        msg: err?.response?.data?.message || err.message || "Update failed",
        sev: "error",
      });
    }
  };

  if (loading || !initialValues) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="EDIT SALE"
        subtitle="Edit existing sale with tax, bonus, and credit"
      />
      <Formik
        initialValues={initialValues}
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(bag) => {
          // Calculate per-item totals and aggregates
          let totalPrice = 0;
          bag.values.items.forEach((item, idx) => {
            const sp =
              getItemSalesPrice(
                items.find((i) => String(i.id) === String(item.itemId))
              ) ||
              Number(item.sp) ||
              0;
            const qty = Number(item.quantity) || 0;
            const bonus = Number(item.bonus) || 0;
            const sponsor = Number(item.sponsor) || 0;

            const bonusAmount = bonus * sp;
            const sponsorAmount = sponsor * sp;
            const total = sp * qty;

            bag.values.items[idx].sp = sp;
            bag.values.items[idx].bonusAmount = bonusAmount;
            bag.values.items[idx].sponsorAmount = sponsorAmount;

            totalPrice += total;
          });

          const vat = +(totalPrice * 0.15).toFixed(2);
          const totalPriceFinal = +(totalPrice + vat).toFixed(2);
          const withholdingAmount = +(totalPrice * 0.03).toFixed(2);

          bag.values.totalPrice = totalPrice;
          bag.values.vat = vat;
          bag.values.totalPriceFinal = totalPriceFinal;
          bag.values.withholdingAmount = withholdingAmount;

          return (
            <form onSubmit={bag.handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                {!isSalesUser && (
                  <TextField
                    select
                    label="User"
                    name="userId"
                    disabled
                    value={bag.values.userId}
                    onChange={async (e) => {
                      bag.handleChange(e);
                      const selectedUserId = e.target.value;

                      // Fetch FS and Machine number for the selected user
                      try {
                        const res = await api.get(
                          `/api/machinfs/user/${selectedUserId}`
                        );
                        const fsRecords = res.data.data || [];
                        if (fsRecords.length > 0) {
                          // Take the latest FS record
                          const latestFS = fsRecords[fsRecords.length - 1];
                          bag.setFieldValue("fsNo", latestFS.fsNo || "");
                          bag.setFieldValue(
                            "machineNo",
                            latestFS.machineNo || ""
                          );
                        } else {
                          bag.setFieldValue("fsNo", "");
                          bag.setFieldValue("machineNo", "");
                        }
                      } catch (err) {
                        console.error("Failed to fetch FS records:", err);
                        bag.setFieldValue("fsNo", "");
                        bag.setFieldValue("machineNo", "");
                      }
                    }}
                    onBlur={bag.handleBlur}
                    variant="filled"
                    fullWidth
                    error={bag.touched.userId && Boolean(bag.errors.userId)}
                    helperText={bag.touched.userId && bag.errors.userId}
                  >
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.fullName || u.username}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  select
                  label="Customer"
                  name="customerId"
                  disabled
                  value={bag.values.customerId}
                  onChange={async (e) => {
                    bag.handleChange(e);
                    const selectedCustomer = customers.find(
                      (c) => c.id === Number(e.target.value)
                    );

                    // Auto-fill TIN if exists
                    try {
                      const customerRes = await api.get(
                        `/api/customer/${selectedCustomer?.id}`
                      );
                      const customerData = customerRes.data;
                      if (customerData?.tinNumber) {
                        bag.setFieldValue("tinNo", customerData.tinNumber);
                      } else {
                        bag.setFieldValue("tinNo", "");
                      }
                    } catch (err) {
                      console.error("Failed to fetch customer:", err);
                      bag.setFieldValue("tinNo", "");
                    }
                  }}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={
                    bag.touched.customerId && Boolean(bag.errors.customerId)
                  }
                  helperText={bag.touched.customerId && bag.errors.customerId}
                >
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name || c.companyName}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Warehouse"
                  name="warehouseId"
                  value={bag.values.warehouseId}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  disabled
                  error={
                    bag.touched.warehouseId && Boolean(bag.errors.warehouseId)
                  }
                  helperText={bag.touched.warehouseId && bag.errors.warehouseId}
                >
                  {warehouses.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Credit"
                  name="credit"
                  value={bag.values.credit ? "true" : "false"}
                  onChange={(e) =>
                    bag.setFieldValue("credit", e.target.value === "true")
                  }
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.credit && Boolean(bag.errors.credit)}
                  helperText={bag.touched.credit && bag.errors.credit}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>

                <TextField
                  label="TIN Number"
                  name="tinNo"
                  value={bag.values.tinNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.tinNo && Boolean(bag.errors.tinNo)}
                  helperText={bag.touched.tinNo && bag.errors.tinNo}
                />

                <TextField
                  label="FS Number"
                  name="fsNo"
                  value={bag.values.fsNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.fsNo && Boolean(bag.errors.fsNo)}
                  helperText={bag.touched.fsNo && bag.errors.fsNo}
                />

                <TextField
                  label="Machine Number"
                  name="machineNo"
                  value={bag.values.machineNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.machineNo && Boolean(bag.errors.machineNo)}
                  helperText={bag.touched.machineNo && bag.errors.machineNo}
                />

                <TextField
                  select
                  label="Bank"
                  name="bank"
                  value={bag.values.bank}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.bank && Boolean(bag.errors.bank)}
                  helperText={bag.touched.bank && bag.errors.bank}
                >
                  {bankOptions.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="date"
                  label="Sales Date"
                  name="salesDate"
                  value={bag.values.salesDate}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.salesDate && Boolean(bag.errors.salesDate)}
                  helperText={bag.touched.salesDate && bag.errors.salesDate}
                />

                <TextField
                  type="number"
                  label="Paid Amount"
                  name="paidAmount"
                  value={bag.values.paidAmount}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={
                    bag.touched.paidAmount && Boolean(bag.errors.paidAmount)
                  }
                  helperText={bag.touched.paidAmount && bag.errors.paidAmount}
                />

                <TextField
                  label="Description (optional)"
                  name="description"
                  value={bag.values.description}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Box>

              {/* Items Section */}
              <FieldArray name="items">
                {({ push, remove }) => (
                  <Box mt={3}>
                    {bag.values.items.map((item, idx) => (
                      <Box key={idx} mb={2}>
                        <Box
                          display="grid"
                          gridTemplateColumns="1.5fr 1fr 1fr 1fr 50px"
                          gap="10px"
                          mb={1}
                        >
                          <TextField
                            select
                            label="Item"
                            name={`items[${idx}].itemId`}
                            value={item.itemId}
                            onChange={bag.handleChange}
                            onBlur={bag.handleBlur}
                            variant="filled"
                            error={
                              bag.touched.items?.[idx]?.itemId &&
                              Boolean(bag.errors.items?.[idx]?.itemId)
                            }
                            helperText={
                              bag.touched.items?.[idx]?.itemId &&
                              bag.errors.items?.[idx]?.itemId
                            }
                          >
                            {items.map((i) => (
                              <MenuItem key={i.id} value={i.id}>
                                {i.name} — SP: {getItemSalesPrice(i)}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            label="SP"
                            value={item.sp}
                            variant="filled"
                            InputProps={{ readOnly: true }}
                          />

                          <TextField
                            type="number"
                            label="Qty"
                            name={`items[${idx}].quantity`}
                            value={item.quantity}
                            onChange={bag.handleChange}
                            onBlur={bag.handleBlur}
                            variant="filled"
                          />

                          <TextField
                            label="Total"
                            value={(item.sp * item.quantity).toFixed(2)}
                            variant="filled"
                            InputProps={{ readOnly: true }}
                          />

                          <Button
                            type="button"
                            color="error"
                            onClick={() => remove(idx)}
                            disabled={bag.values.items.length === 1}
                          >
                            X
                          </Button>
                        </Box>

                        <Box
                          display="grid"
                          gridTemplateColumns="1fr 1fr 1fr 1fr"
                          gap="10px"
                        >
                          <TextField
                            type="number"
                            label="Bonus Units"
                            name={`items[${idx}].bonus`}
                            value={item.bonus}
                            onChange={bag.handleChange}
                            onBlur={bag.handleBlur}
                            variant="filled"
                          />

                          <TextField
                            label="Bonus Amount"
                            value={item.bonusAmount.toFixed(2)}
                            variant="filled"
                            InputProps={{ readOnly: true }}
                          />

                          <TextField
                            type="number"
                            label="Sponsor Units"
                            name={`items[${idx}].sponsor`}
                            value={item.sponsor}
                            onChange={bag.handleChange}
                            onBlur={bag.handleBlur}
                            variant="filled"
                          />

                          <TextField
                            label="Sponsor Amount"
                            value={item.sponsorAmount.toFixed(2)}
                            variant="filled"
                            InputProps={{ readOnly: true }}
                          />
                        </Box>
                      </Box>
                    ))}

                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        push({
                          itemId: "",
                          sp: 0,
                          quantity: 1,
                          bonus: 0,
                          bonusAmount: 0,
                          sponsor: 0,
                          sponsorAmount: 0,
                        })
                      }
                    >
                      + Add Item
                    </Button>
                  </Box>
                )}
              </FieldArray>

              <Box mt={2} display="flex" gap={2}>
                <TextField
                  label="Total Price"
                  value={bag.values.totalPrice.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Total VAT 15%"
                  value={bag.values.vat.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Total Price Final"
                  value={bag.values.totalPriceFinal.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Withholding 3%"
                  value={bag.values.withholdingAmount.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
              </Box>

              <Box display="flex" justifyContent="end" mt={3} gap={2}>
                <Button
                  onClick={() => navigate("/sales/view")}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={bag.isSubmitting}
                >
                  {bag.isSubmitting ? "Updating..." : "Update Sale"}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          severity={toast.sev}
          variant="filled"
          onClose={() => setToast((s) => ({ ...s, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
